/**
 * useAnuncioPt — lo que necesitan los anuncios del «Plano Google Maps 7 Dias»
 * dentro de la app: si se pintan, con qué datos y cómo se abre la ventana.
 *
 * A quién: lo decide el backend (`QR/billing/oferta_pt.py`). `oferta.activa`
 * ya es «pt, sin plan (ni trial), no admin/demo/colaborador, antes del cierre»,
 * así que aquí no se repite ninguna de esas reglas. Al comprar, `activa` pasa a
 * false y todos los anuncios desaparecen solos (nota V5, punto 9).
 *
 * Las franjas de las páginas piden además la ficha conectada (`conGmb`): sin
 * GMB las páginas siguen difuminadas con su banner de conectar, y el anuncio va
 * solo en Dispositivos (nota V5, punto 1).
 *
 * Los datos de la ficha ([X].º en el mapa, negocios por delante, SEO Score,
 * reseñas sin responder) son el diagnóstico de la landing: la misma petición y
 * la misma caché que `/plano-google-maps`, así que la ventana abre ya pintada.
 */
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { getGbusinessMe } from '@/api/gbusinessService';
import { getOfertaPtLanding } from '@/api/ofertaPtService';
import { ME_KEY } from '@/hooks/useOnboarding';
import { useOfertaPt } from '@/hooks/useOfertaPt';

// La misma clave que la landing con sesión (`LANDING_KEY` + 'sessao' en
// `../v4/OfertaPtV4Page.jsx`). Copiada y no importada: importar la página
// arrastraría la landing entera al bundle del panel.
export const LANDING_SESION_KEY = ['oferta-pt', 'landing', 'sessao'];

/** El parámetro que abre la ventana. Lo usan también los correos de Loops. */
export const PARAM_VENTANA = 'plano';
export const VALOR_VENTANA = 'google-maps';

export function useLandingSesion({ enabled }) {
  return useQuery({
    queryKey: LANDING_SESION_KEY,
    queryFn: () => getOfertaPtLanding(),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
    // Mientras el mapa de su zona se calcula, se vuelve a preguntar (como la landing).
    refetchInterval: (q) => (q.state.data?.diagnostico?.estado === 'a_calcular' ? 5000 : false),
  });
}

/**
 * Abre la ventana del plan añadiendo `?plano=google-maps` a la URL actual. Es
 * un push y no un replace: así el «atrás» del móvil la cierra en vez de sacar
 * al usuario de la plataforma (nota de la ventana, punto 4).
 *
 * `origen` es el id del CTA que la abrió. Viaja en el `state` del historial
 * —no en la URL, que se comparte— y la ventana se lo pasa al checkout: el alta
 * se la lleva el botón que la abrió, no el «Começar» de dentro (§124).
 */
export function useAbrirVentana() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback((origen) => {
    const params = new URLSearchParams(location.search);
    params.set(PARAM_VENTANA, VALOR_VENTANA);
    navigate(
      { pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash },
      { state: { ...(location.state || {}), ofertaPtVentana: true, ofertaPtOrigen: origen || null } },
    );
  }, [navigate, location]);
}

export default function useAnuncioPt() {
  const oferta = useOfertaPt();
  const { data: me } = useQuery({ queryKey: ME_KEY, queryFn: getGbusinessMe, retry: false });
  const conGmb = !!me?.connected;
  const { data: landing } = useLandingSesion({ enabled: oferta.activa });
  const abrirVentana = useAbrirVentana();

  return {
    activa: oferta.activa,
    conGmb,
    plazasRestantes: oferta.plazasRestantes,
    plazasTotal: oferta.plazasTotal,
    cierreAt: oferta.cierreAt,
    diagnostico: landing?.diagnostico || null,
    abrirVentana,
  };
}
